import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MarketplaceKeys } from '@config/keys/shop.keys';
import { MarketplaceCartScopeValues } from '@config/values/marketplace.values';
import { BasketStoreRefiner } from '@common/refiners/basket.refiner';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { DoodOrderModel } from '@store/order/order.model';

import {
  CartTipPickerBlockDataSource,
  CartTipPickerBlockViewModel,
} from './cart-tip-picker-block-view-model';

@Injectable()
export class CartTipPickerBlockStoreDataSource implements CartTipPickerBlockDataSource {
  private readonly cartSelector = inject(CartStoreSelector);
  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly basketRefiner = inject(BasketStoreRefiner);
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);

  private readonly tipsAllowed$ = this.cartSelector.selectActive$.pipe(
    switchMap(cart =>
      combineLatest([
        this.shopSelector.selectShop(cart?.shop ?? ''),
        this.marketplaceSelector.selectMarketplace$,
        this.basketRefiner.selectHasBasket,
      ]),
    ),
    map(([shop, marketplace, isBasketExist]) => {
      if (marketplace[MarketplaceKeys.CartScope] === MarketplaceCartScopeValues.Marketplace) {
        return marketplace[MarketplaceKeys.TipsAllowed];
      }
      return !!shop?.tips_allowed && !isBasketExist;
    }),
  );

  readonly vm$: Observable<CartTipPickerBlockViewModel> = combineLatest({
    tipsAllowed: this.tipsAllowed$,
    totalAmount: this.orderSelector.selectValidation$.pipe(
      map(order => ((order as unknown as DoodOrderModel)?.discounted_price_inc_tax ?? 0) / 100),
    ),
    selectedTip: this.orderSelector.selectValidation$.pipe(
      map(order => ((order as unknown as DoodOrderModel)?.tip?.amount ?? 0) / 100),
    ),
  });
}
